import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import makeStyles from "@material-ui/core/styles/makeStyles";
import Card from "@material-ui/core/Card";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import MuiDrawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";

// icons
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";

import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

// import { WithReduxForm, SelectInput, PageFooter, Button, translate, showNotification, required } from "jazasoft";
import { WithReduxForm, SelectInput, PageFooter, Button, translate, showNotification, required } from "jazasoft";

import Table, { NativeTextInput } from "../../../components/Table";
import MuiTable from "../../../components/MuiTable";
import CardHeader from "../../../components/CardHeader";

import { format } from "../../../utils/dateUtils";
import { defaultParsePaste, getDistinctValues, isEmpty, isTrue, toFixed } from "../../../utils/helpers";
import { useDeepEffect, useDeepMemo, useMutation, useTime } from "../../../hooks";
import { getSortedSizeKeyList } from "../../../helpers/orderHelper";
import { REGEX_POSITIVE_NUMBER } from "../../../utils/regex";

const fpoLevelChoices = [
  { id: "BPO", name: "BPO" },
  { id: "COLOR", name: "COLOR" }
];

const NON_SB_FIELDS = [
  "id",
  "orderQty",
  "label",
  "product",
  "style",
  "color",
  "flowRef",
  "delFlowRef",
  "customerFlowRef",
  "inseam",
  "destination",
  "delivery",
  "total"
];

const setFormValues = (flowInfoList, name, form, row) => {
  const productMap = flowInfoList.map(flowInfo => flowInfo.product).reduce((acc, p) => ({ ...acc, [p.id]: p }), {});
  const productList = Object.values(productMap);

  if (productList.length === 1) {
    const product = productList[0];
    form.change(`${name.split(".")[0]}.productId`, product.id);
  } else if (!Object.keys(productMap).includes(`${row.productId}`)) {
    form.change(`${name.split(".")[0]}.productId`, null);
  }

  const styles = getDistinctValues(flowInfoList.map(flowInfo => flowInfo.style));
  if (styles.length === 1) {
    form.change(`${name.split(".")[0]}.style`, styles[0]);
  } else if (styles.includes(row.style)) {
    form.change(`${name.split(".")[0]}.style`, null);
  }

  const colors = getDistinctValues(flowInfoList.map(flowInfo => flowInfo.color));
  if (colors.length === 1) {
    form.change(`${name.split(".")[0]}.color`, colors[0]);
  } else if (!colors.includes(row.color)) {
    form.change(`${name.split(".")[0]}.color`, null);
  }
};

const useStyles = makeStyles(theme => ({
  drawer: ({ drawerWidth }) => ({
    width: drawerWidth,
    flexShrink: 0
  }),
  drawerPaper: ({ drawerWidth }) => ({
    width: drawerWidth
  }),
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center",
    margin: "1.5em"
  },
  content: {
    padding: "0 1.5em"
  },
  table: {
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  },
  rowCell: {
    padding: 0,
    fontSize: 12,
    "&:last-child": {
      padding: 0,
      fontSize: 12
    }
  }
}));

const FPOCreateDrawer = props => {
  const { translate, open, order, unitList, onDrawerClose, onRefresh } = props;
  const classes = useStyles({ drawerWidth: 1080 });

  const [flowInfoList, setFlowInfoList] = useState([]);
  const [breakupRows, setBreakupRows] = useState([]);

  const { utc_offset } = useTime();
  const [mutate, { loading: saving }] = useMutation();
  const dispatch = useDispatch();

  const formValues = useSelector(state => state.form && state.form["record-form"]?.values);
  const fpoLevel = formValues?.fpoLevel;

  useEffect(() => {
    setFlowInfoList([]);
    setBreakupRows([]);
  }, [open]);

  const flowInfoColumns = useDeepMemo(() => {
    const flowInfoList = order?.flowInfoList?.filter(fi => isEmpty(fi.orderParentId)) || [];
    const products = flowInfoList.reduce((acc, fi) => ({ ...acc, [fi.product?.id]: fi.product }), {});
    const styles = getDistinctValues(flowInfoList.map(fi => fi.style));
    const colors = getDistinctValues(flowInfoList.map(fi => fi.color));
    const inseams = getDistinctValues(flowInfoList.map(fi => fi.inseam).filter(e => !isEmpty(e)));
    const destinations = getDistinctValues(flowInfoList.map(fi => fi.destination).filter(e => !isEmpty(e)));
    const deliveryDates = getDistinctValues(flowInfoList.map(fi => fi.deliveryDate).filter(e => !isEmpty(e)));
    const delModes = getDistinctValues(flowInfoList.map(fi => fi.delMode).filter(e => !isEmpty(e)));
    const customerFlowRefs = getDistinctValues(flowInfoList.map(fi => fi.customerFlowRef).filter(e => !isEmpty(e)));
    const flowRefs = getDistinctValues(flowInfoList.map(fi => fi.flowRef).filter(e => !isEmpty(e)));
    const delFlowRefs = getDistinctValues(flowInfoList.map(fi => fi.delFlowRef).filter(e => !isEmpty(e)));

    let cols = [
      {
        dataKey: "productId",
        title: "Product",
        inputType: "select-input",
        choices: Object.values(products).filter(product => !isEmpty(product) && !isTrue(product?.isSet))
      },
      { dataKey: "style", title: "Style", inputType: "select-input", choices: styles.map(style => ({ id: style, name: style })) },
      { dataKey: "color", title: "Color", inputType: "select-input", choices: colors.map(color => ({ id: color, name: color })) },
      { dataKey: "inseam", title: "Inseam", inputType: "select-input", choices: ({ row }) => (!isEmpty(row) ? row?.inseamList || [] : []) },
      {
        dataKey: "destination",
        title: "Destination",
        inputType: "select-input",
        choices: ({ row }) => (!isEmpty(row) ? row?.destinationList || [] : [])
      },
      {
        dataKey: "deliveryDate",
        title: "Delivery Date",
        inputType: "select-input",
        choices: ({ row }) => (!isEmpty(row) ? row?.delDateList || [] : [])
      },
      { dataKey: "delMode", title: "Delivery Mode", inputType: "select-input", choices: ({ row }) => (!isEmpty(row) ? row?.delModeList || [] : []) },
      {
        dataKey: "customerFlowRef",
        title: "CustomerFlowRef",
        inputType: "select-input",
        choices: ({ row }) => (!isEmpty(row) ? row?.customerFlowRefList || [] : [])
      },
      { dataKey: "flowRef", title: "Flow Ref", inputType: "select-input", choices: ({ row }) => (!isEmpty(row) ? row?.flowRefList || [] : []) },
      {
        dataKey: "delFlowRef",
        title: "Del. Flow Ref",
        inputType: "select-input",
        choices: ({ row }) => (!isEmpty(row) ? row?.delFlowRefList || [] : [])
      },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "plannedQty", title: "Planned Qty" },
      { dataKey: "fpoQty", title: "FPO Qty", inputType: "text-input", options: { style: { width: 60 } } },
      { dataKey: "fpoQtyPercent", title: "FPO (%)", render: value => (!isEmpty(value) ? `${value}%` : "") }
    ];

    if (isEmpty(inseams)) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (isEmpty(destinations)) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (isEmpty(deliveryDates)) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (isEmpty(delModes)) {
      cols = cols.filter(c => c.dataKey !== "delMode");
    }
    if (isEmpty(customerFlowRefs)) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (isEmpty(flowRefs)) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (isEmpty(delFlowRefs)) {
      cols = cols.filter(c => c.dataKey !== "delFlowRef");
    }

    if (fpoLevel === "COLOR") {
      cols = cols.filter(c => !["inseam", "destination", "deliveryDate", "delMode", "customerFlowRef", "flowRef", "delFlowRef"].includes(c.dataKey));
    }

    return cols;
  }, [fpoLevel, order]);

  const flowInfoRows = useDeepMemo(() => {
    return [{}];
  }, []);

  const onFlowInfoChange = (name, value, row, form) => {
    const dataKey = name.split(".")[1];
    const rowIdx = Number(name.split(".")[0].replace("rows[", "").replace("]", ""));

    const flowInfoList = order?.flowInfoList?.filter(fi => isEmpty(fi.orderParentId)) || [];
    if (!isEmpty(flowInfoList)) {
      setFormValues(flowInfoList, name, form, { ...row, [dataKey]: value });
    }

    const rows = form.getState()?.values?.rows || [];
    const fRow = { ...rows[rowIdx], [dataKey]: value };

    //** Update BPO related choices *//
    if (!isEmpty(fRow.productId) && !isEmpty(fRow.style) && !isEmpty(fRow.color)) {
      const fiList = flowInfoList
        .filter(fi => fi.product?.id === fRow.productId && fi.style === fRow.style && fi.color === fRow.color)
        .filter(fi => isEmpty(fRow.inseam) || fRow.inseam === fi.inseam)
        .filter(fi => isEmpty(fRow.destination) || fRow.destination === fi.destination)
        .filter(fi => isEmpty(fRow.deliveryDate) || fRow.deliveryDate === fi.deliveryDate)
        .filter(fi => isEmpty(fRow.delMode) || fRow.delMode === fi.delMode)
        .filter(fi => isEmpty(fRow.customerFlowRef) || fRow.customerFlowRef === fi.customerFlowRef)
        .filter(fi => isEmpty(fRow.flowRef) || fRow.flowRef === fi.flowRef)
        .filter(fi => isEmpty(fRow.delFlowRef) || fRow.delFlowRef === fi.delFlowRef);
      const inseams = getDistinctValues(fiList.map(fi => fi.inseam).filter(e => !isEmpty(e))).map(e => ({ id: e, name: e }));
      const destinations = getDistinctValues(fiList.map(fi => fi.destination).filter(e => !isEmpty(e))).map(e => ({ id: e, name: e }));
      const deliveryDates = getDistinctValues(fiList.map(fi => fi.deliveryDate).filter(e => !isEmpty(e))).map(e => ({ id: e, name: e }));
      const delModes = getDistinctValues(fiList.map(fi => fi.delMode).filter(e => !isEmpty(e))).map(e => ({ id: e, name: e }));
      const customerFlowRefs = getDistinctValues(fiList.map(fi => fi.customerFlowRef).filter(e => !isEmpty(e))).map(e => ({ id: e, name: e }));
      const flowRefs = getDistinctValues(fiList.map(fi => fi.flowRef).filter(e => !isEmpty(e))).map(e => ({ id: e, name: e }));
      const delFlowRefs = getDistinctValues(fiList.map(fi => fi.delFlowRef).filter(e => !isEmpty(e))).map(e => ({ id: e, name: e }));

      form.change(`${name.split(".")[0]}.inseamList`, inseams);
      form.change(`${name.split(".")[0]}.destinationList`, destinations);
      form.change(`${name.split(".")[0]}.delDateList`, deliveryDates);
      form.change(`${name.split(".")[0]}.delModeList`, delModes);
      form.change(`${name.split(".")[0]}.customerFlowRefList`, customerFlowRefs);
      form.change(`${name.split(".")[0]}.flowRefList`, flowRefs);
      form.change(`${name.split(".")[0]}.delFlowRefList`, delFlowRefs);

      form.change(`${name.split(".")[0]}.inseam`, inseams?.length === 1 ? inseams[0].id : null);
      form.change(`${name.split(".")[0]}.destination`, destinations?.length === 1 ? destinations[0].id : null);
      form.change(`${name.split(".")[0]}.deliveryDate`, deliveryDates?.length === 1 ? deliveryDates[0].id : null);
      form.change(`${name.split(".")[0]}.delMode`, delModes?.length === 1 ? delModes[0].id : null);
      form.change(`${name.split(".")[0]}.customerFlowRef`, customerFlowRefs?.length === 1 ? customerFlowRefs[0].id : null);
      form.change(`${name.split(".")[0]}.flowRef`, flowRefs?.length === 1 ? flowRefs[0].id : null);
      form.change(`${name.split(".")[0]}.delFlowRef`, delFlowRefs?.length === 1 ? delFlowRefs[0].id : null);
    }

    //** Update Order Qty *//
    const rowList = form.getState()?.values?.rows || [];
    const finalRow = { ...rowList[rowIdx], [dataKey]: value };
    let finalFlowInfoList = [];
    let fpoFlowInfoList = [];
    if (fpoLevel === "BPO") {
      finalFlowInfoList = flowInfoList
        .filter(fi => fi.isLeaf)
        .filter(fi => fi.product?.id === finalRow.productId && fi.style === finalRow.style && fi.color === finalRow.color)
        .filter(fi => (!isEmpty(finalRow.inseam) ? finalRow.inseam === fi.inseam : isEmpty(fi.inseam) || finalRow.inseam === fi.inseam))
        .filter(fi =>
          !isEmpty(finalRow.destination)
            ? finalRow.destination === fi.destination
            : isEmpty(fi.destination) || finalRow.destination === fi.destination
        )
        .filter(fi =>
          !isEmpty(finalRow.deliveryDate)
            ? finalRow.deliveryDate === fi.deliveryDate
            : isEmpty(fi.deliveryDate) || finalRow.deliveryDate === fi.deliveryDate
        )
        .filter(fi => (!isEmpty(finalRow.delMode) ? finalRow.delMode === fi.delMode : isEmpty(fi.delMode) || finalRow.delMode === fi.delMode))
        .filter(fi =>
          !isEmpty(finalRow.customerFlowRef)
            ? finalRow.customerFlowRef === fi.customerFlowRef
            : isEmpty(fi.customerFlowRef) || finalRow.customerFlowRef === fi.customerFlowRef
        )
        .filter(fi => (!isEmpty(finalRow.flowRef) ? finalRow.flowRef === fi.flowRef : isEmpty(fi.flowRef) || finalRow.flowRef === fi.flowRef))
        .filter(fi =>
          !isEmpty(finalRow.delFlowRef) ? finalRow.delFlowRef === fi.delFlowRef : isEmpty(fi.delFlowRef) || finalRow.delFlowRef === fi.delFlowRef
        );
      fpoFlowInfoList = order?.flowInfoList
        ?.filter(fi => !isEmpty(fi.fpo) && !isTrue(fi.cancelled))
        .filter(fi => fi.product?.id === finalRow.productId && fi.style === finalRow.style && fi.color === finalRow.color)
        .filter(fi => (!isEmpty(finalRow.inseam) ? finalRow.inseam === fi.inseam : isEmpty(fi.inseam) || finalRow.inseam === fi.inseam))
        .filter(fi =>
          !isEmpty(finalRow.destination)
            ? finalRow.destination === fi.destination
            : isEmpty(fi.destination) || finalRow.destination === fi.destination
        )
        .filter(fi =>
          !isEmpty(finalRow.deliveryDate)
            ? finalRow.deliveryDate === fi.deliveryDate
            : isEmpty(fi.deliveryDate) || finalRow.deliveryDate === fi.deliveryDate
        )
        .filter(fi => (!isEmpty(finalRow.delMode) ? finalRow.delMode === fi.delMode : isEmpty(fi.delMode) || finalRow.delMode === fi.delMode))
        .filter(fi =>
          !isEmpty(finalRow.customerFlowRef)
            ? finalRow.customerFlowRef === fi.customerFlowRef
            : isEmpty(fi.customerFlowRef) || finalRow.customerFlowRef === fi.customerFlowRef
        )
        .filter(fi => (!isEmpty(finalRow.flowRef) ? finalRow.flowRef === fi.flowRef : isEmpty(fi.flowRef) || finalRow.flowRef === fi.flowRef))
        .filter(fi =>
          !isEmpty(finalRow.delFlowRef) ? finalRow.delFlowRef === fi.delFlowRef : isEmpty(fi.delFlowRef) || finalRow.delFlowRef === fi.delFlowRef
        );
    } else {
      finalFlowInfoList = flowInfoList
        .filter(fi => isEmpty(fi.parentId))
        .filter(fi => fi.product?.id === finalRow.productId && fi.style === finalRow.style && fi.color === finalRow.color);
      fpoFlowInfoList = order?.flowInfoList
        ?.filter(fi => !isEmpty(fi.fpo) && !isTrue(fi.cancelled))
        .filter(fi => fi.product?.id === finalRow.productId && fi.style === finalRow.style && fi.color === finalRow.color);
    }
    const orderQty = finalFlowInfoList.reduce((acc, fi) => acc + fi.orderQty, 0);
    const plannedQty = (fpoFlowInfoList || []).reduce((acc, fi) => acc + fi.fpoQty, 0);
    const fpoQty = Number(finalRow?.fpoQty || 0);
    const fpoQtyPercent = orderQty > 0 ? toFixed((fpoQty * 100) / orderQty) : 0;
    form.change(`${name.split(".")[0]}.id`, finalFlowInfoList?.length === 1 ? finalFlowInfoList[0]?.id : null);
    form.change(`${name.split(".")[0]}.orderQty`, orderQty);
    form.change(`${name.split(".")[0]}.plannedQty`, plannedQty);
    form.change(`${name.split(".")[0]}.fpoQtyPercent`, fpoQtyPercent);

    //** Set Selected FlowInfoList **/
    form.change(`${name.split(".")[0]}.${dataKey}`, value); // Update current value explicitly
    const finalRowList = form.getState()?.values?.rows?.filter(row => !isEmpty(row.id)) || [];
    setFlowInfoList(finalRowList.map(row => ({ ...flowInfoList.find(e => e.id === row.id), ...row })));
  };

  const onBreakupChange =
    ({ rowIdx, column }) =>
    e => {
      const value = e?.target?.value || e?.value;

      setBreakupRows(prev => {
        let newRows = [...prev];
        let row;
        const data = defaultParsePaste(value) || [];
        if (data.length === 0 || (data.length === 1 && data[0].length === 1)) {
          // Restricting input of String value in size rows
          let allowInput = !isNaN(String(value)) || isEmpty(value) || REGEX_POSITIVE_NUMBER.test(value);
          if (allowInput) {
            row = { ...prev[rowIdx], [column.dataKey]: value };

            const sizeList = Object.keys(row)
              .filter(key => !NON_SB_FIELDS.includes(key))
              .reduce((acc, key) => ({ ...acc, [key]: row[key] }), {});
            let sum = 0;
            for (let size in sizeList) {
              // To avoid warning during summation of size with undefined values
              if (sizeList[size]) {
                sum += Number(sizeList[size]);
              }
            }
            row = { ...row, total: sum };

            newRows.splice(rowIdx, 1, row);
          }
        }
        if (newRows?.length > 1) {
          const sbRows = newRows?.length > 1 ? newRows.slice(0, newRows?.length - 1) : [];

          const totalRow = breakupColumns
            .filter(c => c.dataKey === "total" || !NON_SB_FIELDS.includes(c.dataKey))
            .reduce((acc, c) => ({ ...acc, [c.dataKey]: sbRows.reduce((acc, row) => acc + Number(row[c.dataKey] || 0), 0) }), {});
          return [...sbRows, totalRow];
        }

        return [...newRows];
      });
    };

  //** Prepare Size Breakup Columns for View mode **/
  const { columns: breakupColumns, headerRows } = useDeepMemo(() => {
    if (isEmpty(flowInfoList)) return { columns: [], headerRows: [] };

    const hasInseam = flowInfoList.some(c => !isEmpty(c.inseam));
    const sizeBreakupList = flowInfoList
      .flatMap(flowInfo => {
        const childList = order?.flowInfoList?.filter(fi => fi.parentId === flowInfo?.id) || [];
        return isEmpty(childList) ? [flowInfo] : childList;
      })
      .flatMap(fi => fi.sizeBreakupList?.filter(sb => sb.type === "ORDER") || [])
      .map(sb => (hasInseam ? { ...sb, sizeGroup: "G1", sizeKey: `G1_${sb.size}` } : sb));

    const sbMap = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sbList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);
    let columns = [];
    if (flowInfoList?.length > 1) {
      let cols = [
        { dataKey: "product", title: "Product", align: "center" },
        { dataKey: "style", title: "Style", align: "center" },
        { dataKey: "color", title: "Color", align: "center" },
        { dataKey: "flowRef", title: "Flow Ref", align: "center" },
        { dataKey: "delFlowRef", title: "Del. Flow Ref", align: "center" },
        { dataKey: "customerFlowRef", title: "Customer Flow Ref", align: "center" },
        { dataKey: "inseam", title: "Inseam", align: "center" },
        { dataKey: "destination", title: "Destination", align: "center" },
        { dataKey: "delivery", title: "Delivery Date", align: "center" },
        { dataKey: "delMode", title: "Delivery Mode", align: "center" }
      ];
      const productCount = getDistinctValues(flowInfoList.map(c => c.product?.id))?.length;
      const styleCount = getDistinctValues(flowInfoList.map(c => c.style))?.length;
      const colorCount = getDistinctValues(flowInfoList.map(c => c.color))?.length;
      const flowRefCount = getDistinctValues(flowInfoList.map(c => c.flowRef).filter(e => !isEmpty(e)))?.length;
      const delFlowRefCount = getDistinctValues(flowInfoList.map(c => c.delFlowRef).filter(e => !isEmpty(e)))?.length;
      const cFlowRefCount = getDistinctValues(flowInfoList.map(c => c.customerFlowRef).filter(e => !isEmpty(e)))?.length;
      const inseamCount = getDistinctValues(flowInfoList.map(c => c.inseam).filter(e => !isEmpty(e)))?.length;
      const destCount = getDistinctValues(flowInfoList.map(c => c.destination).filter(e => !isEmpty(e)))?.length;
      const deliveryCount = getDistinctValues(flowInfoList.map(c => c.deliveryDate).filter(e => !isEmpty(e)))?.length;
      const delModeCount = getDistinctValues(flowInfoList.map(c => c.delMode).filter(e => !isEmpty(e)))?.length;

      if (productCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "product");
      }
      if (styleCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "style");
      }
      if (colorCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "color");
      }
      if (flowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "flowRef");
      }
      if (delFlowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "delFlowRef");
      }
      if (cFlowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "customerFlowRef");
      }
      if (inseamCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "inseam");
      }
      if (destCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "destination");
      }
      if (deliveryCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "delivery");
      }
      if (delModeCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "delMode");
      }
      columns = [...cols];
    }

    const sizeKeys = getSortedSizeKeyList(sbList);
    let groupRow = {};
    let sizeList = [];
    let sizeGroup;
    for (let i = 0; i < sizeKeys.length; i++) {
      const sizeKey = sizeKeys[i];
      const sb = sbList.find(sb => sb.sizeKey === sizeKey);
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sizeKey] = sb.sizeGroup;
      }
      sizeList.push({
        dataKey: sizeKey,
        title: sizeKey.split("_")[1],
        leftBorder,
        element: (
          <NativeTextInput
            style={{ textAlign: "center" }}
            onChange={onBreakupChange}
            disabled={({ record, column, rowIdx, colIdx }) => isEmpty(record?.id) || column.dataKey === "total"}
          />
        )
      });
    }

    const haveSizeGroup = !hasInseam && getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 0;

    columns = [
      ...columns,
      ...sizeList,
      {
        dataKey: "total",
        title: "Total",
        leftBorder: true,
        element: (
          <NativeTextInput
            style={{ textAlign: "center" }}
            onChange={onBreakupChange}
            disabled={({ record, column, rowIdx, colIdx }) => isEmpty(record?.id) || column.dataKey === "total"}
          />
        )
      }
    ];
    columns = columns.map(c => ({
      ...c,
      align: "center",
      colSpan: ({ column, row, rowIdx }) =>
        !haveSizeGroup || rowIdx === 1 || NON_SB_FIELDS.includes(c.dataKey)
          ? 1
          : isEmpty(row[column.dataKey])
          ? 0
          : getDistinctValues(sbList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
    }));

    let headerRows = [];
    if (!hasInseam && Object.keys(groupRow)?.length > 0) {
      headerRows = [
        groupRow,
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    } else {
      headerRows = [
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    }

    return { columns, headerRows };
  }, [flowInfoList]);

  //** Prepare Size Breakup Rows for View mode **/
  useDeepEffect(() => {
    if (isEmpty(flowInfoList)) return;

    const hasInseam = flowInfoList.some(c => !isEmpty(c.inseam));
    let rows = flowInfoList.map(fi => {
      const orderQty = fi.orderQty;
      const fpoQty = fi.fpoQty || 0;
      const fraction = orderQty > 0 ? fpoQty / orderQty : 0;

      const childList = order?.flowInfoList?.filter(e => e.parentId === fi?.id) || [];
      const fiList = isEmpty(childList) ? [fi] : childList;

      const sizeBreakupList = fiList.flatMap(fi => fi.sizeBreakupList?.filter(sb => sb.type === "ORDER") || []);
      const sbMap = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: { ...sb, qty: (acc[sb.sizeKey]?.qty || 0) + sb.qty } }), {});
      const sbList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);

      return {
        id: fi.id,
        product: fi.product?.name,
        color: fi.color,
        flowRef: fi.flowRef,
        delFlowRef: fi.delFlowRef,
        customerFlowRef: fi.customerFlowRef,
        inseam: fi.inseam,
        destination: fi.destination,
        delivery: !isEmpty(utc_offset) && !isEmpty(fi.exFactoryDate) ? `${format(fi.exFactoryDate, utc_offset, "dd MMM, yy")}` : "",
        total: sbList?.reduce((acc, sb) => acc + Math.round(sb.qty * fraction), 0),
        orderQty: fi.orderQty,
        ...sbList
          ?.map(sb => (hasInseam ? { ...sb, sizeKey: `G1_${sb.size}` } : sb))
          .reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: Math.round(sb.qty * fraction) }), {})
      };
    });
    if (rows.every(row => row.total === 0)) {
      rows = [];
    }
    if (rows?.length > 1 && !isEmpty(flowInfoList)) {
      const totalRow = breakupColumns
        .filter(c => c.dataKey === "total" || !NON_SB_FIELDS.includes(c.dataKey))
        .reduce((acc, c) => ({ ...acc, [c.dataKey]: rows.reduce((acc, row) => acc + (row[c.dataKey] || 0), 0) }), {});
      rows.push(totalRow);
    }
    setBreakupRows(rows);
  }, [flowInfoList, flowInfoColumns]);

  const onSubmit = (rows, _, onActionComplete) => {
    const fpoData = {
      ...formValues,
      flowInfoList: flowInfoList.map(fi => {
        const sbRow = breakupRows.find(row => row.id === fi.id);
        const sizeBreakupList = breakupColumns
          .filter(c => !NON_SB_FIELDS.includes(c.dataKey))
          .map((c, idx) => ({
            serialNo: idx + 1,
            sizeGroup: !isEmpty(fi.inseam) ? fi.inseam : c.dataKey?.split("_")[0],
            size: c.dataKey?.split("_")[1],
            qty: sbRow[c.dataKey]
          }))
          .filter(sb => sb.qty > 0);

        return {
          productId: fi.productId || fi.product?.id,
          style: fi.style,
          color: fi.color,
          inseam: fi.inseam,
          destination: fi.destination,
          deliveryDate: fi.deliveryDate,
          delMode: fi.delMode,
          flowRef: fi.flowRef,
          delFlowRef: fi.delFlowRef,
          customerFlowRef: fi.customerFlowRef,
          orderQty: fi.fpoQty,
          overPlannedQty: fi.plannedQty + Number(fi.fpoQty) > fi.orderQty ? fi.plannedQty + Number(fi.fpoQty) - fi.orderQty : 0,
          sizeBreakupList
        };
      })
    };

    // if (fpoData.flowInfoList.some(fi => fi.overPlannedQty > 0)) {
    //   dispatch(
    //     showNotification("Over Planning. Overall Planning cannot exceed Order Qty. Hint: Check total planning in each FlowInfo", { type: "danger" })
    //   );
    //   onActionComplete && onActionComplete(rows);
    //   return;
    // }

    const options = {
      url: "fpo",
      method: "POST",
      params: { orderId: order?.id },
      data: fpoData
    };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("FPO created successfully."));
        onRefresh && onRefresh();
        onDrawerClose();
      },
      onFailure: () => {
        onActionComplete && onActionComplete(rows);
      }
    });
  };

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  return (
    <MuiDrawer
      className={classes.drawer}
      anchor="right"
      open={open}
      onClose={onDrawerClose}
      classes={{
        paper: classes.drawerPaper
      }}>
      <div className={classes.drawerHeader}>
        <IconButton onClick={onDrawerClose}>
          <ChevronRightIcon />
        </IconButton>

        <Typography style={{ marginLeft: "0.5em" }} variant="h5">
          Create FPO
        </Typography>
      </div>
      <Divider />
      <div className={classes.content}>
        <WithReduxForm initialValues={{ fpoLevel: order?.fpoLevel || "BPO" }}>
          {({ handleSubmit }) => (
            <div>
              <Grid container spacing={2} style={{ marginBottom: "1.5em" }}>
                {/* <Grid item xs={12} sm={4}>
                  <TextInput label="resources.fpo.fields.fpo" source="fpo" fullWidth validate={required()} />
                </Grid> */}
                <Grid item xs={12} sm={4}>
                  <SelectInput
                    label="resources.fpo.fields.fpoLevel"
                    source="fpoLevel"
                    choices={fpoLevelChoices}
                    fullWidth
                    disabled={!isEmpty(order.fpoLevel)}
                    validate={required()}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <SelectInput label="resources.fpo.fields.unitId" source="unitId" choices={unitList} fullWidth validate={required()} />
                </Grid>
              </Grid>

              <Card className={classes.marginVertical}>
                <CardHeader title={translate(`resources.orders.cardHeader.flowInfo`, { _: "Flow Info" })} />
                <Divider />
                <div>
                  <MuiTable
                    columns={flowInfoColumns}
                    rows={flowInfoRows}
                    component="div"
                    editable
                    editing
                    showEditableActions
                    rowAddCount={1}
                    onRowAdd={() => {}}
                    handleSubmitRef={handleSubmitRef}
                    onChange={onFlowInfoChange}
                    onSubmit={onSubmit}
                    i18nKey="resources.orders.fields.flowInfo"
                  />
                </div>
              </Card>

              <Card style={{ marginTop: "1.5em" }}>
                <CardHeader title="Size Breakup" />

                <Divider />
                <PerfectScrollbar>
                  <Table
                    classes={{ table: classes.table, rowCell: classes.rowCell }}
                    columns={breakupColumns}
                    headerRows={headerRows}
                    rows={breakupRows}
                    i18nKey="resources.orders.fields.flowInfo"
                    headerCellStyle={column => ({
                      width: NON_SB_FIELDS.includes(column?.dataKey) ? 150 : undefined,
                      borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
                      fontWeight: "bold",
                      backgroundColor: "#C6D0DA"
                    })}
                    cellStyle={({ column, record }) => ({
                      fontWeight: record.isTotalRow ? "bold" : "normal",
                      borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
                    })}
                  />
                </PerfectScrollbar>
              </Card>

              {/* Footer */}
              <PageFooter className={classes.pageFooter}>
                <Button
                  label={translate("resources.orders.action.save", { _: "SAVE" })}
                  color="primary"
                  variant="contained"
                  disabled={saving}
                  onClick={event => {
                    handleSubmit(() => {})();
                    ffHandleSubmit(event);
                  }}>
                  <SaveIcon />
                  {saving && <CircularProgress size={20} thickness={2} />}
                </Button>
                <Button
                  style={{ marginLeft: "1em" }}
                  label={translate("resources.orders.action.cancel", { _: "CANCEL" })}
                  color="primary"
                  variant="outlined"
                  disabled={saving}
                  onClick={onDrawerClose}>
                  <CancelIcon />
                </Button>
              </PageFooter>
            </div>
          )}
        </WithReduxForm>
      </div>
    </MuiDrawer>
  );
};

export default translate(FPOCreateDrawer);
